import { isOidcAuthorizePath } from './oidc-authorize';
import { parseDomain } from './parse-domain';
import { SettingsCookie } from './settings-cookie';

export function goToLastPage(): void {
   window.location.assign(getLastPage());
}

export function getLastPage(): string {
   // Public-suffix development tunnels cannot share the return-page cookie.
   // Preserve the explicit OAuth request without decoding its nested query twice.
   const requestedLastPage = getRequestedLastPage();
   if (requestedLastPage) {
      try {
         const requestedUrl = new URL(requestedLastPage, window.location.origin);
         if (
            requestedUrl.origin === window.location.origin &&
            isOidcAuthorizePath(requestedUrl.pathname)
         ) {
            return requestedLastPage;
         }
      } catch {
         // An invalid return URL falls back to the existing cookie behavior.
      }
   }

   // If this was opened in a new window (e.g., target="_blank" rel="noreferrer"), then
   // close it and refresh the original window. The assumption is that this
   // behavior is more desirable that redirecting to last viewed page if
   // the query parameter close_after is set.
   if (
      new URLSearchParams(window.location.search).get('close_after') === '1' &&
      window.opener &&
      window.opener.location.hostname === window.location.hostname
   ) {
      window.opener.location.reload();
      window.close();
   }

   const loc = SettingsCookie.get('last_page') || '/';

   let decoded;
   try {
      decoded = decodeURIComponent(loc);
   } catch {
      return '/';
   }

   return isSafeRedirect(decoded) ? decoded : '/';
}

/**
 * The return page a link or redirect asked for. Links to /Join put it in the
 * URL hash, which browsers don't send, so every /Join request shares one
 * cached page. /Login and older links still send it in the query.
 */
export function getRequestedLastPage(): string | null {
   const fromHash = new URLSearchParams(window.location.hash.slice(1)).get('last_page');
   return fromHash ?? new URLSearchParams(window.location.search).get('last_page');
}

function isSafeRedirect(url: string): boolean {
   let parsed;
   try {
      parsed = new URL(url, window.location.origin);
   } catch {
      return false;
   }

   if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
   }

   const currentDomain = parseDomain()['domain'];
   return (
      parsed.hostname === window.location.hostname || parsed.hostname.endsWith(`.${currentDomain}`)
   );
}
